import { Link } from "react-router-dom";
import { useMemo } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSEO } from "@/hooks/useCanonical";
import { getStaticRouteSeo } from "@/lib/seo";
import { HEJ_MAMMA_FAQ_ITEMS, buildHejMammaJsonLd } from "@/lib/structuredData";

const HejMamma = () => {
  const faqItems = HEJ_MAMMA_FAQ_ITEMS;

  const { data: images = [] } = useQuery({
    queryKey: ["hej-mamma-images"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("images")
        .select(`
          url,
          caption,
          sort_order,
          jobs!inner(slug)
        `)
        .eq("jobs.slug", "hej-mamma")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data || [];
    }
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ["hej-mamma-jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select(`
          id,
          title,
          slug,
          description,
          categories!inner(slug),
          images(url, sort_order)
        `)
        .eq("categories.slug", "hej-mamma")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data || []).filter((job: any) => job.slug !== "hej-mamma" && job.images && job.images.length > 0);
    }
  });

  // Create JSON-LD dynamically when images are loaded
  const jsonLd = useMemo(
    () => buildHejMammaJsonLd({ imageUrls: images.map((img: { url: string }) => img.url) }),
    [images],
  );

  const seo = getStaticRouteSeo("hejMamma", { image: images[0]?.url });

  useSEO({ ...seo, jsonLd });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      <main className="pt-32 md:pt-40 pb-24 flex-1">
        {/* Hero with Featured Image */}
        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <div className="gap-6 md:gap-10 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-small-heading font-extrabold italic tracking-tight mb-8">
                Mammaporträtt och gravidfoto i Stockholm
              </h1>
              <p className="text-2xl md:text-3xl font-small-heading font-extrabold italic tracking-tight mb-4">
                Hej Mamma!
              </p>
              <p className="text-lg md:text-xl leading-relaxed text-foreground/85">
                Mammaporträtt hemma hos dig – för dig som väntar barn, har en bebis, eller vill ha bilder tillsammans
                med dina barn. Gravidfoto, mamma & barn, eller hela familjen.
              </p>
            </div>
          </div>
        </section>

        {/* Image grid - all images small and together */}
        {images.length > 1 && (
          <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
            <div className="grid grid-cols-3 gap-3 md:gap-4">
              {images.slice(1, 7).map((img, index) => (
                <div key={index} className="aspect-[3/4] overflow-hidden">
                  <img
                    src={img.url}
                    alt={img.caption || `Gravidfoto bebisfoto mammaporträtt ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* The Story – Why I do this */}
        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <div className="space-y-6 text-foreground/85 leading-relaxed">
            <p>
              Det var när jag fick min son 2020 som jag bestämde mig för att börja fotografera mammor. Under
              graviditeten fantiserade jag om alla fina konstnärliga bilder jag skulle ta på mig och min son tillsammans
              med min analoga mellanformatskamera. Så blev det inte.
            </p>

            <p>
              Sömnbrist och långa dagar i en liten mörk lägenhet. Jag kände mig trött, sliten och inte särskilt snygg
              under hela mammaledigheten – så det blev inte många bilder tagna. Nu några år senare är jag otroligt
              tacksam över de få som finns. Trötthet och hormoner lurar för när jag ser tillbaka på de bilderna ser
              jag inte alls ut som jag kände mig.
            </p>
            <p>
              En natt när jag låg vaken och ammade min son tänkte jag på familjealbum och mamma och barnporträtt. Jag
              funderade kring hur jag hade velat att bilderna skulle se ut om jag blivit fotograferad med min son, men
              också vad dessa bilder faktiskt betyder längre fram i livet för både mamma och barn. Jag tänkte på att
              jag bara har ett fåtal bilder på mig och min egen mamma från barndomen. Och att det
              ofta är vi mammor som står bakom kameran men sällan framför den. Jag kände att det är något jag vill
              hjälpa till att ändra på.
            </p>
          </div>
        </section>

        {/* What to Expect */}
        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16">
            <div>
              <h2 className="text-base font-small-heading font-extrabold italic text-foreground mb-6">Du kan boka</h2>
              <div className="space-y-4 text-foreground/85 leading-relaxed">
                <p>
                  <strong className="text-foreground">Gravidfoto</strong> – du och magen, med eller utan partner.
                </p>
                <p>
                  <strong className="text-foreground">Mamma & bebis / mamma & barn</strong> – hemma hos er eller
                  utomhus.
                </p>
                <p>
                  <strong className="text-foreground">Hela resan</strong> – vi ses först när du är gravid, och igen när
                  bebisen är här.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-base font-small-heading font-extrabold italic text-foreground mb-6">
                Så går det till
              </h2>
              <div className="space-y-4 text-foreground/85 leading-relaxed">
                <p>
                  En fotografering tar oftast en timme, där vi hänger hemma hos er (eller utomhus) och jag fångar er på
                  bild. Under fotograferingen fokuserar jag på naturlighet och känsla, inte poser. Men jag guidar dig
                  hela vägen.
                </p>
                <p>
                  Efter fotograferingen laddar jag upp bilderna i ett privat webbgalleri där du kan välja bilder i lugn
                  och ro. Då väljer du ett av följande paket:
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <div>
            <h2 className="text-cl font-small-heading font-extrabold italic text-foreground mb-6 max-w-3xl mx-auto text-xl">
              Paket och priser
            </h2>
            <div className="grid md:grid-cols-3 gap-4 mb-12">
              {/* Lilla paketet */}
              <div className="wiggly-border p-6">
                <h3 className="font-small-heading font-extrabold italic mb-2 text-lg">Lilla paketet</h3>
                <p className="mb-6 font-normal text-xl">4 200 kr</p>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  Fotografering (ca 1 timme) + 5 redigerade digitala bilder både högupplösta och webbanpassade.
                  <br />
                  Är du självstående mamma på heltid? Skriv det vid bokning så får du 15% rabatt.
                </p>
              </div>

              {/* Stora paketet */}
              <div className="wiggly-border p-6">
                <h3 className="font-small-heading font-extrabold italic mb-2 text-lg">Stora paketet</h3>
                <p className="mb-6 font-normal text-xl">7 500 kr</p>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  Fotografering (ca 1 timme) <br />+ ca 50 redigerade digitala bilder
                  <br />
                  både högupplösta och webbanpassade
                </p>
              </div>

              {/* Dubbel-paketet */}
              <div className="wiggly-border p-6 rounded-sm">
                <h3 className="font-small-heading font-extrabold italic mb-2 text-lg">Dubbel-paketet</h3>
                <p className="mb-6 text-xl font-normal">7 900 kr</p>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  Två fototillfällen (gravid + bebis) <br />+ 10 redigerade digitala bilder
                  <br />
                  både högupplösta och webbanpassade
                </p>
              </div>
            </div>

            <p className="text-foreground/85 leading-relaxed text-left max-w-3xl mx-auto mb-12">
              Du väljer paket utifrån hur många bilder du vill ha.
              <br />
              Du kan göra ditt val innan eller efter fotograferingen när du sett bilderna i ditt privata galleri.
            </p>

            {/* CTA */}
            <div className="text-center mb-8">
              <p className="text-lg text-foreground/85 mb-8">
                Vill du boka en fotografering, eller bara ställa en fråga? Hör av dig.
              </p>
              <Button asChild size="lg" className="bg-red-500 text-white hover:bg-red-600 px-8 py-6 text-base font-medium">
                <Link to="/booking">Skicka förfrågan</Link>
              </Button>
            </div>

            {/* Betalning & villkor */}
            <div className="bg-secondary/20 p-6 rounded-sm">
              <h3 className="font-small-heading font-extrabold italic text-lg mb-4">Fler bilder?</h3>
              <p className="text-foreground/70 text-sm leading-relaxed mb-4">
                450 kr/bild eller 1 600 kr för 5 extra bilder. Det kan du välja efter du sett bilderna.
              </p>
              <h3 className="font-small-heading font-extrabold italic text-lg mb-4">Betalning & villkor</h3>
              <ul className="text-foreground/60 text-xs leading-relaxed space-y-2 list-disc pl-4 marker:text-foreground/30">
                <li className="text-xs">När du bokar skickas en faktura på en bokningsavgift på 1 500 kr</li>
                <li className="text-xs">
                  Bokningsavgiften är ej återbetalningsbar vid avbokning, men det går bra att boka om tiden vid sjukdom
                  eller dåligt väder
                </li>
                <li className="text-xs">
                  Bokningsavgiften dras av från slutfakturan, som skickas i samband med bildleveransen
                </li>
                <li>Slutfakturan ska betalas senast 30 dagar efter fotograferingsdagen</li>
                <li>Fotograferingar sker i första hand på vardagar</li>
                <li>Vid fotografering på helg tillkommer ett helgtillägg på 1 000 kr</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
          <h2 className="text-xl font-small-heading font-extrabold italic text-foreground mb-8">Vanliga frågor</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index + 1}`} className="border-b border-foreground/10">
                <AccordionTrigger className="text-left text-base hover:no-underline py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/70 leading-relaxed pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Portfolio Links */}
        {jobs.length > 0 && (
          <section className="container mx-auto px-6 md:px-8 max-w-4xl mb-12 md:mb-16">
            <h2 className="text-xl font-small-heading font-extrabold italic text-foreground mb-8">Tidigare jobb</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
              {jobs.slice(0, 9).map((job: any) => {
                const firstImage = job.images?.sort((a: any, b: any) => a.sort_order - b.sort_order)[0];
                return (
                  <Link key={job.id} to={`/work/hej-mamma/${job.slug}`} className="group">
                    <div className="aspect-[3/4] overflow-hidden mb-3">
                      {firstImage && (
                        <img src={firstImage.url} alt={job.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <p className="text-sm text-foreground/100 group-hover:text-foreground transition-colors inline-block border-b border-transparent group-hover:border-red-500">
                      {job.title}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default HejMamma;
